import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Button,
  IconButton,
  HStack,
  VStack,
  Text,
  Checkbox,
  Badge,
  Skeleton,
  useToast,
} from "@chakra-ui/react";
import { CurrencyText, EmptyState } from "../../../shared/ui";
import { ChevronLeft, ChevronRight, Repeat } from "../../../shared/icons";
import { adicionarMeses, formatarMesAno } from "../../../shared/lib/date";
import { isPlanoBloqueado } from "../../../shared/api/planoBloqueado";
import { apiErrorMessage } from "../../../shared/api/apiErrorMessage";
import { useShiftPatternPreviewQuery, useGenerateScheduleMutation } from "../hooks/shiftPatternQueries";
import { formatScheduleDate } from "../utils/describeShiftPattern";

const itemKey = (item) => `${item.vinculo_id}-${item.data}`;
const toRequestItem = (item) => ({ vinculo_id: item.vinculo_id, data: item.data });

// Prévia da escala fixa de um mês: as datas ainda não registradas já vêm marcadas, o
// médico desmarca o que não vai acontecer (folga, troca) e confirma. As desmarcadas são
// gravadas como puladas — voltam desmarcadas e o lembrete do Dashboard para de cobrar
// por elas. O que já existe aparece só para conferência: gerar de novo nunca duplica.
export default function GenerateScheduleModal({ isOpen, onClose, initialYear, initialMonth }) {
  const toast = useToast();
  const navigate = useNavigate();
  const [period, setPeriod] = useState({ year: initialYear, month: initialMonth });
  useEffect(() => {
    if (isOpen) setPeriod({ year: initialYear, month: initialMonth });
  }, [isOpen, initialYear, initialMonth]);

  const { data, isLoading } = useShiftPatternPreviewQuery(period, { enabled: isOpen });
  const items = useMemo(() => data?.items ?? [], [data]);
  const pending = useMemo(() => items.filter((item) => !item.already_registered), [items]);

  const [selected, setSelected] = useState(() => new Set());
  useEffect(() => setSelected(new Set(pending.filter((item) => !item.skipped).map(itemKey))), [pending]);

  const chosen = pending.filter((item) => selected.has(itemKey(item)));
  const unchosen = pending.filter((item) => !selected.has(itemKey(item)));
  const total = chosen.reduce((acc, item) => acc + item.valor, 0);
  const mutation = useGenerateScheduleMutation();

  const toggle = (key) =>
    setSelected((current) => {
      const next = new Set(current);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  const moveMonth = (delta) =>
    setPeriod((p) => {
      const { ano, mes } = adicionarMeses(p.year, p.month, delta);
      return { year: ano, month: mes };
    });

  const monthLabel = formatarMesAno(period.year, period.month);

  const generate = () =>
    mutation.mutate(
      { items: chosen.map(toRequestItem), skipped: unchosen.map(toRequestItem) },
      {
        onSuccess: (result) => {
          const created = result?.created ?? chosen.length;
          toast({
            title: created > 0 ? `Escala de ${monthLabel} gerada` : `Escala de ${monthLabel} conferida`,
            description: `${created} plantão(ões) registrado(s)${unchosen.length > 0 ? `, ${unchosen.length} data(s) pulada(s)` : ""}.`,
            status: "success",
            duration: 5000,
          });
          onClose();
        },
        onError: (error) => {
          if (isPlanoBloqueado(error)) return;
          toast({
            title: "Não foi possível gerar a escala",
            description: apiErrorMessage(error),
            status: "error",
            duration: 5000,
            isClosable: true,
          });
        },
      }
    );

  let body;
  if (isLoading) {
    body = <Skeleton height="200px" borderRadius="md" />;
  } else if (items.length === 0) {
    body = (
      <EmptyState
        icon={Repeat}
        title="Nenhum plantão fixo neste mês"
        description="Cadastre a escala fixa (semanal ou quinzenal) na página de cada vínculo e volte aqui para gerar os plantões do mês."
        action={
          <Button
            size="sm"
            variant="outline"
            onClick={() => {
              onClose();
              navigate("/vinculos");
            }}
          >
            Ir para Vínculos
          </Button>
        }
      />
    );
  } else {
    body = (
      <VStack align="stretch" spacing={1}>
        {pending.length === 0 ? (
          <Text fontSize="sm" color="app-muted-fg" mb={2}>
            Todos os plantões fixos deste mês já estão registrados.
          </Text>
        ) : (
          <Text fontSize="sm" color="app-muted-fg" mb={2}>
            Desmarque as datas que não vão acontecer (folga, troca): elas ficam como puladas.
          </Text>
        )}
        {items.map((item) => {
          const key = itemKey(item);
          return (
            <HStack key={key} justify="space-between" py={1.5} borderBottom="1px solid" borderColor="app-border">
              <Checkbox
                isChecked={item.already_registered || selected.has(key)}
                isDisabled={item.already_registered}
                onChange={() => toggle(key)}
              >
                <Text fontSize="sm">
                  <Text as="span" fontWeight="medium">
                    {formatScheduleDate(item.data)}
                  </Text>{" "}
                  · {item.vinculo_nome}
                </Text>
              </Checkbox>
              <HStack spacing={2} flexShrink={0}>
                {item.already_registered ? (
                  <Badge variant="statusSubtle" colorScheme="gray">
                    Já registrado
                  </Badge>
                ) : item.skipped ? (
                  <Badge variant="statusSubtle" colorScheme="orange">
                    Pulado
                  </Badge>
                ) : null}
                <Text fontSize="sm" color={item.already_registered ? "app-muted-fg" : undefined}>
                  <CurrencyText value={item.valor} />
                </Text>
              </HStack>
            </HStack>
          );
        })}
      </VStack>
    );
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg" isCentered scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Gerar escala do mês</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <HStack justify="space-between" mb={4}>
            <IconButton aria-label="Mês anterior" icon={<ChevronLeft size={18} />} variant="ghost" size="sm" onClick={() => moveMonth(-1)} />
            <Text fontWeight="semibold">{monthLabel}</Text>
            <IconButton aria-label="Próximo mês" icon={<ChevronRight size={18} />} variant="ghost" size="sm" onClick={() => moveMonth(1)} />
          </HStack>
          {body}
        </ModalBody>
        <ModalFooter gap={3} justifyContent="space-between">
          <Text fontSize="sm" color="app-muted-fg">
            {chosen.length > 0 ? (
              <>
                {chosen.length} plantão(ões) · <CurrencyText value={total} />
              </>
            ) : null}
          </Text>
          <HStack spacing={3}>
            <Button variant="ghost" onClick={onClose} isDisabled={mutation.isPending}>
              Cancelar
            </Button>
            <Button onClick={generate} isDisabled={pending.length === 0} isLoading={mutation.isPending}>
              {chosen.length > 0
                ? `Gerar ${chosen.length} plantão(ões)`
                : pending.length > 0
                  ? `Pular ${unchosen.length} data(s)`
                  : "Gerar escala"}
            </Button>
          </HStack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
